import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Card, Grid, Container, Box } from "@mui/material";
import { keyframes } from "@mui/system";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import fotoAntiga from "assets/images/historia/foto-antiga.jpg";
import fotoAtual from "assets/images/historia/foto-atual.jpg";

/* =========================
   Paleta/estilos da marca
   ========================= */
const DEFAULT_BRAND = {
  primary: "#036D3D",
  primaryDark: "#004C2A",
  primaryLight: "#05A055",
  accent: "#FFD700",
  gradient: "linear-gradient(135deg, #036D3D 0%, #05A055 100%)",
  glowPrimary: "rgba(3,109,61,0.35)",
  glowAccent: "rgba(255,215,0,0.25)",
  line: "rgba(3,109,61,0.22)",
  surface: "rgba(255,255,255,0.96)",
  cardShadow: "0 8px 30px rgba(3,109,61,0.08)",
  cardHoverShadow: "0 18px 50px rgba(3,109,61,0.16)",
};

/* =========================
   Hook: aparecer no scroll
   ========================= */
const useScrollAnimation = (threshold = 0.15) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          io.unobserve(entry.target);
        }
      },
      { threshold }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, isVisible];
};

/* =========================
   Keyframes globais
   ========================= */
const pulseYear = keyframes`
  0%,100% { transform: scale(1);   opacity: .28; }
  50%     { transform: scale(1.12); opacity: .12; }
`;
const popIn = keyframes`
  0%   { transform: translateY(24px); opacity: 0; }
  100% { transform: translateY(0);    opacity: 1; }
`;


/* =========================
   Sub: “bolacha” do ano
   ========================= */
const AnimatedYear = ({ year, index, brand }) => {
  const [yearRef, show] = useScrollAnimation(0.25);

  return (
    <Box
      ref={yearRef}
      sx={{
        position: "absolute",
        zIndex: 4, // fica acima do Card
        pointerEvents: "none",
        left: { xs: -42, md: -56 },
        top: { xs: 14, md: 18 },
        width: { xs: 64, md: 82 },
        height: { xs: 64, md: 82 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: brand.gradient,
        borderRadius: "50%",
        color: "#fff",
        fontWeight: 900,
        fontSize: { xs: "1rem", md: "1.2rem" },
        letterSpacing: "0.4px",
        
        transform: show ? "scale(1)" : "scale(0.6)",
        opacity: show ? 1 : 0,
        transition: "transform .6s cubic-bezier(0.34,1.56,0.64,1), opacity .6s",
        transitionDelay: `${index * 160}ms`,
        "&::before": {
          content: '""',
          position: "absolute",
          inset: -8,
          borderRadius: "50%",
          background: brand.gradient,
          opacity: 0.28,
          animation: show ? `${pulseYear} 2.2s ease-in-out infinite` : "none",
        },
      }}
    >
      <span style={{zIndex:999999999,color: '#fff'}}>{year}</span>
    </Box>
  );
};

AnimatedYear.propTypes = {
  year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  index: PropTypes.number.isRequired,
  brand: PropTypes.object.isRequired,
};

/* =========================
   Sub: Card do marco
   ========================= */
const MilestoneCard = ({ milestone, index, brand, isLast }) => {
  const [cardRef, show] = useScrollAnimation(0.2);

  return (
    <Box
      ref={cardRef}
      sx={{
        position: "relative",
        overflow: "visible", // não corta a bolacha do ano
        mb: isLast ? 0 : { xs: 4, md: 6 },
        ml: { xs: 4, md: 6 },
        
      }}
    >
      <AnimatedYear year={milestone.ano} index={index} brand={brand} />

      <Card
        sx={{
          position: "relative",
          zIndex: 3, // abaixo do ano
          marginLeft: 2,
          background: brand.surface,
          backdropFilter: "blur(14px)",
          border: "1px solid rgba(255,255,255,0.22)",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: brand.cardShadow,
          transform: show ? "none" : "translateY(24px)",
          opacity: show ? 1 : 0,
          animation: show ? `${popIn} .65s cubic-bezier(.25,.46,.45,.94) forwards` : "none",
          animationDelay: `${index * 140 + 120}ms`,
          "&:hover": {
            transform: "translateY(-6px)",
            boxShadow: brand.cardHoverShadow,
            "& .milestone-image": { transform: "scale(1.05)" },
          },
        }}
      >
        {milestone.img && (
          <MKBox sx={{ position: "relative", overflow: "hidden", height: { xs: 190, md: 220 } }}>
            <MKBox
              component="img"
              className="milestone-image"
              src={milestone.img}
              alt={milestone.labelTop}
              loading="lazy"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform .5s ease",
                ...(index === 0 && { filter: "sepia(.12) contrast(1.05) brightness(.98)" }),
              }}
            />
            <MKBox
              sx={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(180deg,transparent 60%,rgba(0,0,0,.25) 100%)",
              }}
            />
          </MKBox>
        )}

        <MKBox sx={{ p: { xs: 2.25, md: 3 } }}>
          

          <MKTypography
            variant="body1"
            sx={{
              color: "text.secondary",
              lineHeight: 1.85,
              textAlign: "justify",
              fontSize: { xs: ".97rem", md: "1rem" },
              "& strong": { color: brand.primaryDark, fontWeight: 700 },
            }}
            dangerouslySetInnerHTML={{
              __html: milestone.text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>"),
            }}
          />
        </MKBox>
      </Card>
    </Box>
  );
};

MilestoneCard.propTypes = {
  milestone: PropTypes.shape({
    ano: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    labelTop: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    img: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  brand: PropTypes.object.isRequired,
  isLast: PropTypes.bool,
};

/* =========================
   Principal (50% texto / 50% timeline)
   ========================= */
export default function NossaHistoria({
  brand = DEFAULT_BRAND,
  topSticky = { xs: "80px", md: "110px" },
  containerSx,
  ...rest
}) {
  const [leftRef, showLeft] = useScrollAnimation(0.1);

  // Texto com “Quem Somos” e “Compromisso” integrados
const blocks = [
  { type: "h", text: "Nossa História" },

  // introdução da história
  {
    type: "p",
    text:
      "Desde a fundação, evoluímos com investimentos contínuos em tecnologia e capacitação, mantendo o **compromisso com inovação**, **qualidade** e **relacionamento** de longo prazo com **clientes** e **parceiros**.",
  },

  // seção: Quem Somos
  { type: "sub", text: "Quem Somos" },
  {
    type: "p",
    text:
      "Atendendo o mercado, a **Ramlis Machine atua** no segmento de usinagem de precisão em **diversos setores industriais**. Com uma equipe formada por profissionais capacitados, que empenham-se em alcançar a qualidade máxima, segurança e uma maior resultado para o produto final.",
  },

  // seção: Compromisso com a Qualidade
  { type: "sub", text: "Compromisso com a Qualidade" },
  {
    type: "p",
    text:
      "A Empresa conta com funcionários especializados na área, que junto com os equipamentos de última geração utilizados no processo de produção, nos permitem a **Garantia de Qualidade de nossos produtos**, lema principal de nossa empresa.",
  },
];


  // APENAS 2010 e 2020
  const milestones = [
    {
      key: "fundacao",
      ano: 2010,
      labelTop: "Fundação",
      text:
        "**Início das atividades** com foco em usinagem de precisão e proximidade com os primeiros clientes.",
      img: fotoAntiga,
    },
    {
      key: "modernizacao",
      ano: 2020,
      labelTop: "Modernização",
      text:
        "**Evoluímos** o parque industrial e ampliamos a capacidade para demandas mais complexas.",
      img: fotoAtual,
    },
  ];

  return (
    <MKBox
      component="section"
      sx={{
        backgroundBlendMode: "normal, overlay, overlay",
        py: { xs: 6, md: 10 },
        ...containerSx,
      }}
      {...rest}
    >
      <Container sx={{ maxWidth: 1200, mx: "auto" }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="flex-start">
          {/* 50% TEXTO */}
          <Grid item xs={12} md={6}>
            <Box
              ref={leftRef}
              sx={{
                position: { md: "sticky" },
                top: topSticky,
                transition: "all .6s ease",
                transform: showLeft ? "translateY(0)" : "translateY(16px)",
                opacity: showLeft ? 1 : 0,
              }}
            >
              {blocks.map((b, i) => {
                if (b.type === "h") {
                  return (
                    <MKBox key={`h-${i}`} sx={{ mb: 2, position: "relative" }}>
                      <MKTypography
                        variant="h3"
                        sx={{
                          fontWeight: 900,
                          background: brand.gradient,
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          lineHeight: 1.2,
                        }}
                      >
                        {b.text}
                      </MKTypography>
                      <MKBox sx={{ width: 80, height: 4, bgcolor: brand.primary, borderRadius: 2, mt: 1 }} />
                    </MKBox>
                  );
                }
                if (b.type === "sub") {
                  return (
                    <MKTypography
                      key={`sub-${i}`}
                      variant="button"
                      sx={{
                        display: "block",
                        mt: 3,
                        mb: 1,
                        fontWeight: 800,
                        letterSpacing: ".8px",
                        textTransform: "uppercase",
                        color: brand.primaryDark,
                        fontSize: ".92rem",
                        position: "relative",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: -14,
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: 8,
                          height: 8,
                          background: brand.gradient,
                          borderRadius: "2px",
                        },
                      }}
                    >
                      {b.text}
                    </MKTypography>
                  );
                }
                return (
                  <MKTypography
                    key={`p-${i}`}
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 2,
                      textAlign: "justify",
                      fontSize: { xs: ".98rem", md: "1.02rem" },
                      "& strong": { color: brand.primaryDark, fontWeight: 700 },
                      mb: 2,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: b.text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>"),
                    }}
                  />
                );
              })}
            </Box>
          </Grid>

          {/* 50% TIMELINE (vertical) */}
          <Grid item xs={12} md={6}>
            <MKBox
              sx={{
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: { xs: 34, md: 44 },
                  top: 0,
                  bottom: 0,
                  width: 2,
                  background: `linear-gradient(180deg, transparent 0%, ${brand.line} 12%, ${brand.primary} 50%, ${brand.line} 88%, transparent 100%)`,
                },
              }}
            >
              {milestones.map((m, idx) => (
                <MilestoneCard
                  key={m.key}
                  milestone={m}
                  index={idx}
                  brand={brand}
                  isLast={idx === milestones.length - 1}
                />
              ))}
            </MKBox>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

NossaHistoria.propTypes = {
  brand: PropTypes.shape({
    primary: PropTypes.string,
    primaryDark: PropTypes.string,
    primaryLight: PropTypes.string,
    accent: PropTypes.string,
    gradient: PropTypes.string,
    glowPrimary: PropTypes.string,
    glowAccent: PropTypes.string,
    line: PropTypes.string,
    surface: PropTypes.string,
    cardShadow: PropTypes.string,
    cardHoverShadow: PropTypes.string,
  }),
  topSticky: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  containerSx: PropTypes.object,
};
